/*
 * Copyright (c) 2021 Pegasystems Inc.
 * All rights reserved.
 */

import AssetLoader from '../utils/asset-loader';
import Utils from '../utils/utils';
import { invokeRestApi } from '../rest-client';
import PubSubUtils from '../utils/pubsub-utils';
import constants from '../constants';

import MessageSubscriber from './message-subscriber';
import UserPresence from './user-presence';
import caseSubscription from './case-subscription';
import type { Filter } from './types';
import AgentMessageHandler from './agent-message-handler';

/**
 * @description
 * Exposes APIs for websocket Messaging Service.
 * @public
 */
class MessagingServiceManager {
  /**
   * {
   *  "<subID>": <MessageSubscriber>
   * }
   */
  static subscribers: { [key: string]: MessageSubscriber } = {};

  static connectionsCount = 0;

  static webSocket: WebSocket | null = null;

  static connectionConfig: {
    messagingService?: string;
    disableReInit?: boolean;
    [key: string]: any;
  } | null = null;

  static inProgressSubscriptions: {
    messageSubscriber: MessageSubscriber;
    id: string;
  }[] = [];

  static inProgressMessages: Record<string, unknown>[] = [];

  static isServiceAlive: boolean | null = null;

  static serviceAliveHandler: ReturnType<typeof setTimeout> | null = null;

  static userPresence = new UserPresence();

  static refreshB2SOffset = 10; // 10secs
  private static tokenExpiryTime: number;
  private static reInitInterval?: ReturnType<typeof setTimeout> | string | number;
  private static agentSubscribeIdMap: Map<string, string> = new Map<string, string>();
  // must follow ws lifecycle
  private static isAuthenticated = false;
  // Stored bound handler references so removeEventListener can match them
  private static boundHandleOnOpen: (event: Event) => void;
  private static boundHandleOnMessage: (event: MessageEvent) => void;
  private static boundHandleOnError: (event: Event) => void;
  private static boundHandleOnClose: (event: CloseEvent) => void;
  /**
   * Initializing the connection to message service.
   * @param config object to initialize connection to messaging service
   * example:
   * {
   *  messagingService: "ws://localhost:3000/c11n-msg"
   * }
   * @private
   */
  static initConnection(config?: { messagingService?: string } | null) {
    if (!config) {
      return;
    }
    const { messagingService } = config;
    if (!messagingService || (messagingService && messagingService.length <= 6)) {
      return;
    }

    const token = AssetLoader.b2sJWT;
    // check should really be ~500, however unit tests use 'test' as token
    if (!token || token.length < 4) {
      return;
    }

    // if existing socket, handlers and socket should be cleaned up first
    if (this.webSocket) {
      this.webSocket.close();
      this.cleanSocketListeners();
    }

    // Create WebSocket connection.

    const wsPath = messagingService.endsWith('/') ? `${messagingService}ws` : `${messagingService}/ws`;
    try {
      const socket = new WebSocket(`${wsPath}`);
      this.webSocket = socket;
      // Bind once and store so the same reference is used in removeEventListener
      this.boundHandleOnOpen = this.handleOnOpen.bind(this);
      this.boundHandleOnMessage = this.handleOnMessage.bind(this);
      this.boundHandleOnError = this.handleOnError.bind(this);
      this.boundHandleOnClose = this.handleOnClose.bind(this);

      // Connection opened
      socket.addEventListener('open', this.boundHandleOnOpen);

      // Listen for messages
      socket.addEventListener('message', this.boundHandleOnMessage);

      // Connection error
      socket.addEventListener('error', this.boundHandleOnError);

      // Connection closed
      socket.addEventListener('close', this.boundHandleOnClose);
    } catch (e) {
      console.log(e);
    }
  }

  static handleOnError(event: Event) {
    if (this.getTokenExpiryTime() <= 0) {
      clearInterval(this.reInitInterval);
      this.reInitInterval = undefined;
      // must follow ws lifecycle
      this.isAuthenticated = false;
    }
    console.log('web socket connection error event: ', event);
  }

  static handleOnClose(event: CloseEvent) {
    // clear liveness check
    if (this.serviceAliveHandler !== null) {
      clearTimeout(this.serviceAliveHandler);
      this.serviceAliveHandler = null;
    }

    // must follow ws lifecycle
    this.isAuthenticated = false;

    /*
      Custom Codes:
      4001: Invalid Token
    */
    if (event && event.code === 4001) {
      return;
    }

    if (this.connectionConfig?.disableReInit) {
      return;
    }

    // do not start reconnect sequence if restart is already in-flight or if it does not have valid token
    if (this.reInitInterval || this.getTokenExpiryTime() <= 0) {
      return;
    }
    this.reInitialization();
  }

  static handleOnOpen() {
    clearInterval(this.reInitInterval);
    this.reInitInterval = undefined;
    if (this.webSocket !== null && this.webSocket.readyState === 1) {
      const token = AssetLoader.b2sJWT;
      // check should really be ~500, however unit tests use 'test' as token
      if (!token || token.length < 4) {
        return;
      }
      this.publishMessage({ token });
    }
  }

  static unsubscribeAgentMessageCaseValues(dispatchData: {
    caseKey: string;
    assignmentID: string;
    actionInContext: string;
  }) {
    const subscriptionId = MessagingServiceManager.agentSubscribeIdMap.get(dispatchData.assignmentID);
    MessagingServiceManager.unsubscribe(subscriptionId ?? '');
    MessagingServiceManager.agentSubscribeIdMap.delete(dispatchData.assignmentID);
  }

  static subscribeAgentMessageCaseValues(dispatchData: {
    caseKey: string;
    assignmentID: string;
    actionInContext: string;
  }) {
    const filter = {
      matcher: 'COACH_UPDATE',
      criteria: {
        caseId: dispatchData.caseKey,
        assignmentId: dispatchData.assignmentID
      }
    };
    const agentSubscribeId = MessagingServiceManager.subscribe(
      filter,
      (data: { message: { [key: string]: unknown } }) => {
        AgentMessageHandler.publishCaseValuesUpdate(data, dispatchData);
      }
    );
    MessagingServiceManager.agentSubscribeIdMap.set(dispatchData.assignmentID, agentSubscribeId);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  static handleOnMessage(event: { data: string }) {
    if (event !== null && event.data === 'ALIVE') {
      if (this.isServiceAlive === null) {
        this.checkLiveliness(10000);
      }
      this.isServiceAlive = true;
    } else {
      let data: {
        msgId: any;
        filter: { matcher: any; criteria: any };
        tokenExpiryTime: any;
      } | null = null;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        console.error('Invalid data received from messaging service', event.data);
      }

      // respond with an ack to confirm service delivery
      if (data && data.msgId) {
        this.webSocket?.send(`{"ack":"${data.msgId}"}`);
      }

      if (!data || !data.filter) {
        if (data && data.tokenExpiryTime) {
          // must follow ws lifecycle
          this.isAuthenticated = true;
          PubSubUtils.subscribe(
            constants.PUB_SUB_EVENTS.CASE_EVENTS.ASSIGNMENT_OPENED,
            MessagingServiceManager.subscribeAgentMessageCaseValues
          );
          PubSubUtils.subscribe(
            constants.PUB_SUB_EVENTS.CASE_EVENTS.END_OF_ASSIGNMENT_PROCESSING,
            MessagingServiceManager.unsubscribeAgentMessageCaseValues
          );
          this.setTokenExpiryTime(data);
          const refreshInMs = (this.getTokenExpiryTime() - this.refreshB2SOffset) * 1000;
          setTimeout(() => this.refreshB2STokenReInit(), refreshInMs);
          let ids: string[] | null = [];
          this.inProgressSubscriptions.forEach(({ messageSubscriber, id }) => {
            if (!ids?.includes(id)) {
              ids?.push(id);
              this.subscribeToSocket(messageSubscriber, id);
            }
          });
          this.inProgressSubscriptions = [];
          this.inProgressMessages.forEach((message) => {
            this.publishMessage(message);
          });
          this.inProgressMessages = [];
          ids = null;
        } else {
          console.error('filter missing from received data from messaging service', event.data);
        }
      } else {
        const { matcher, criteria } = data.filter;
        // remove environmentID from criteria
        delete criteria.environmentID;

        Object.keys(this.subscribers).forEach((subId) => {
          const sub = this.subscribers[subId];
          if (sub?.filter && sub.filter.matcher === matcher && Utils.isEqual(sub.filter.criteria, criteria)) {
            sub.messageHandler(data);
          }
        });
      }
    }
  }

  /**
   * Api to reInitialize connection on abrupt closing of connection or ALIVE message not received in specified time.
   * @param skipSetInterval passed true if broadcast message not received in given time period
   * @private
   */
  static reInitialization(skipSetInterval?: boolean) {
    // Timer in progress
    if (this.reInitInterval) {
      return;
    }

    // unsubscribe, to remove subscription from server, to reduce count and avoid duplicate subscription
    Object.keys(this.subscribers).forEach((subId) => {
      this.unsubscribe(subId, true);
    });

    // Push to queue
    Object.keys(this.subscribers).forEach((subId) => {
      this.inProgressSubscriptions.push({
        messageSubscriber: this.subscribers[subId],
        id: subId
      });
    });
    if (!skipSetInterval) {
      this.reInitInterval = setInterval(() => {
        this.initConnection(this.connectionConfig);
      }, 5500);
    } else {
      this.initConnection(this.connectionConfig);
    }
  }

  /**
   * Api to check server liveliness broadcast message based on time passed as argument, to receive recurring ALIVE message on network
   * @param timeToPing  time in ms.
   * @private
   */
  static checkLiveliness(timeToPing: number) {
    if (this.isServiceAlive === false) {
      this.reInitialization(true);
    } else {
      this.isServiceAlive = false;
    }
    if (this.serviceAliveHandler !== null) clearTimeout(this.serviceAliveHandler);
    this.serviceAliveHandler = setTimeout(() => {
      this.checkLiveliness(timeToPing);
    }, timeToPing);
  }

  /**
   * Sets the messaging service url
   * @param connectionConfig  service url to be connected for websocket.
   * eq.,
   * {
   *  messagingService: "ws://localhost:3000/c11n-msg"
   * }
   * @private
   */
  static setConnectionConfig(connectionConfig: { [key: string]: any }) {
    this.connectionConfig = connectionConfig;
  }

  /**
   * Api to remove all listener attached on websocket
   * @private
   */
  static cleanSocketListeners() {
    if (this.webSocket !== null) {
      this.webSocket.removeEventListener('open', this.boundHandleOnOpen);
      this.webSocket.removeEventListener('message', this.boundHandleOnMessage);
      this.webSocket.removeEventListener('error', this.boundHandleOnError);
      this.webSocket.removeEventListener('close', this.boundHandleOnClose);
    }
  }

  /**
   * Api to clean and nullify websocket
   * @private
   */
  static disconnect() {
    if (this.webSocket !== null) {
      this.cleanSocketListeners();
      this.webSocket.close();
      this.webSocket = null;
    }
  }

  /**
   * Api to subscribe to messaging service and depends on the filter creteria messages will
   * be forwarded to subscribers.
   * @param filter Object to hold filter creteria,
   * It helps to receive only targetted changes.
   * eq.,
   * 1.
   * {
   *  matcher: "interaction"
   * }
   *
   * 2.
   * {
   *  matcher: "pulse",
   *  filter: {
   *      caseId: "EPIC-201"
   *  }
   * }
   * @param messageHandler callback handler
   * @param contextName - Name of the context eq., app/primary_1
   * @param id any specific id or id will be autogenerated. Id can be used for unsubscribing from
   * message service.
   * @example <caption>Example subscribe()</caption>
   * Example usage - PCore.getMessagingServiceManager().subscribe({matcher: "interaction"}, message => {
   *  // Do process message here
   * });
   * @returns returns the subscription id, can be used for unsubscribing from
   * message service.
   * @public
   */
  static subscribe(filter: Filter, messageHandler: Function, contextName?: string, id?: string) {
    this.connectionsCount += 1;
    const retId = id || `${Date.now()}_${this.connectionsCount}`;

    const messageSubscriber = new MessageSubscriber(filter, messageHandler, contextName);

    if (this.webSocket !== null && this.webSocket.readyState === 1 && this.isAuthenticated) {
      this.subscribeToSocket(messageSubscriber, retId);
      return retId;
    }

    // Push to queue
    this.inProgressSubscriptions.push({
      messageSubscriber,
      id: retId
    });

    // If connection not initialized do init.
    if (this.webSocket === null) {
      this.initConnection(this.connectionConfig);
    }

    return retId;
  }

  /**
   * Subscribes to socket.
   * @param messageSubscriber message subscriber
   * @param id subscriber id
   * @private
   */
  static subscribeToSocket(messageSubscriber: MessageSubscriber, id: string) {
    this.subscribers[id] = messageSubscriber;

    const subscriptionMessage = {
      filter: messageSubscriber.filter,
      subscribe: {
        id
      }
    };
    this.webSocket?.send(JSON.stringify(subscriptionMessage));
  }

  /**
   * Removes the handler from subscriptions and disconnects from service.
   * @param id Pass id returned by subscribe method.
   * @example <caption>Example unsubscribe()</caption>
   * const subId = PCore.getMessagingServiceManager().subscribe({matcher: "interaction"}, message => {
   *  // Do process message here
   * }));
   * PCore.getMessagingServiceManager().unsubscribe(subId);
   * @public
   */
  static unsubscribe(id: string, notRemove?: boolean) {
    if (this.subscribers[id]) {
      if (!notRemove) {
        delete this.subscribers[id];
      }
      const unSubMessage = {
        unsubscribe: {
          id
        }
      };

      // TODO : Need to improve implementation, check this with some time delays or with callbacks
      if (this.webSocket !== null && this.webSocket.readyState === 1) {
        this.webSocket.send(JSON.stringify(unSubMessage));
      }
    }
  }

  /**
   * Publishes the message to subscribers received from server.
   * @param message message object received from messaging service.
   * @private
   */
  static publishMessage(message: Record<string, unknown>) {
    if (this.webSocket === null) {
      console.error('Web socket not initialized.');
      return;
    }

    if (this.webSocket !== null && this.webSocket.readyState === 1) {
      this.webSocket.send(JSON.stringify(message));
      return;
    }

    this.inProgressMessages.push(message);
  }

  static getUserPresence = () => this.userPresence;

  static getCaseSubscription = (caseId: string) => {
    return caseSubscription.getOrCreateSubscription(caseId);
  };

  static refreshB2STokenReInit = async () => {
    const payload = {
      body: {
        B2SToken: AssetLoader.b2sJWT // Expired b2sJWT Token
      }
    };

    invokeRestApi('refreshB2S', payload)
      .then((response) => {
        const { newB2SToken } = response.data;
        if (newB2SToken && newB2SToken.length > 30) {
          AssetLoader.b2sJWT = newB2SToken;
          this.setTokenExpiryTime({ tokenExpiryTime: Math.round(Date.now() / 1000) + 60 }); // this is to re-init connection again with new token
          this.webSocket?.close();
        } else {
          console.log('refreshB2S: invalid b2s length');
        }
      })
      .catch((err) => {
        console.log(`refreshB2S: error occurred while fetching: ${err}`);
      });
  };

  static getTokenExpiryTime = () => {
    const currTimeInSeconds = Math.round(Date.now() / 1000);
    return this.tokenExpiryTime - currTimeInSeconds;
  };

  static setTokenExpiryTime = (token: { tokenExpiryTime: number }) => {
    this.tokenExpiryTime = token.tokenExpiryTime;
  };

  /**
   * Removes the cache of all subscribers.
   * @param contextName name of the context.
   * @private
   */
  static clearContext(contextName: string) {
    Object.keys(this.subscribers).forEach((subId) => {
      const sub = this.subscribers[subId];
      if (sub.contextName === contextName) {
        this.unsubscribe(subId);
      }
    });
    this.userPresence.clearContext(contextName);
  }
}

export default MessagingServiceManager;
